import {
  Box,
  ButtonBase,
  ButtonBaseProps,
  InputBase,
  InputBaseProps,
  SxProps,
  Theme,
  ThemeProvider,
  Tooltip,
} from '@mui/material';
import { forwardRef, ReactNode, useMemo } from 'react';

import { Icon, IconName } from '@/components/common/Icon';
import { Switch } from '@/components/common/Switch';
import { createAppTheme } from '@/theme';
import { TOOLS_THEME, tx } from '@/theme/tokens';

// Hauser's design system: the one version of each control and surface, in the brand-family language shared with
// Tradehaus (docs/design-language/). Each component takes its sizes, radius, type and colour from the tokens, so a
// screen never sets its own. A one-off on a screen is migrated onto these, not copied. /dev/library draws every one,
// in every state, in all three themes.
//
// Rules the components encode:
// - Tonal fills, never outlines or darker greys. Lime is the one primary action per screen, and flat.
// - Selected / pressed is milled in (deboss); only floating layers (sheets, menus, dialogs) are raised.
// - Four radii: sheet 24, tile 16, control 12, thumb 9. Controls are 40px (32 compact), never pills.
// - Type roles: title 28/300, eyebrow 12/600, label 12/500, field and button 13/600, body 13/500, meta 11/500.

type Sx = SxProps<Theme>;
const merge = (...parts: (Sx | undefined | false)[]): Sx =>
  parts.filter(Boolean).flatMap((p) => (Array.isArray(p) ? p : [p])) as Sx;

const press = {
  transition: `background-color .15s ${tx.ease}, color .15s ${tx.ease}, box-shadow .15s ease, transform .1s ease`,
  '&:active': { transform: 'scale(0.985)' },
  '&.Mui-disabled': { opacity: 0.4 },
  '&.Mui-focusVisible': { outline: '2px solid var(--tx-focus)', outlineOffset: 2 },
};

export const selectedSx = { background: tx.selBg, boxShadow: tx.selShadow, color: tx.text } as const;

const glyph = (icon: IconName | ReactNode | undefined, size: number) =>
  typeof icon === 'string' ? <Icon name={icon as IconName} size={size} /> : icon;

// ── Materials ───────────────────────────────────────────────────────────────

// ToolsSurface: everything inside is slate, whatever the mode (Hauser's sidebar, chat and tasks). It switches both the
// CSS variables and the MUI theme, so tokens and MUI components inside agree. Renders no box of its own.
export const ToolsSurface = ({ children }: { children: ReactNode }) => {
  const theme = useMemo(() => createAppTheme(TOOLS_THEME), []);
  return (
    <ThemeProvider theme={theme}>
      <Box data-theme={TOOLS_THEME} sx={{ display: 'contents' }}>
        {children}
      </Box>
    </ThemeProvider>
  );
};

// ── Text roles ──────────────────────────────────────────────────────────────

export const Eyebrow = ({ children, sx }: { children: ReactNode; sx?: Sx }) => (
  <Box sx={merge({ fontSize: 12, fontWeight: 600, lineHeight: 1.3, color: tx.muted }, sx)}>{children}</Box>
);

export const Title = ({ children, as = 'h2', sx }: { children: ReactNode; as?: 'h1' | 'h2' | 'h3'; sx?: Sx }) => (
  <Box
    component={as}
    sx={merge(
      { m: 0, fontSize: 28, fontWeight: 300, letterSpacing: '-0.035em', lineHeight: 1.12, color: tx.text },
      sx,
    )}>
    {children}
  </Box>
);

export const Label = ({ children, htmlFor, sx }: { children: ReactNode; htmlFor?: string; sx?: Sx }) => (
  <Box component="label" htmlFor={htmlFor} sx={merge({ fontSize: 12, fontWeight: 500, color: tx.muted }, sx)}>
    {children}
  </Box>
);

export const Meta = ({ children, sx }: { children: ReactNode; sx?: Sx }) => (
  <Box component="span" sx={merge({ fontSize: 11, fontWeight: 500, color: tx.faint }, sx)}>
    {children}
  </Box>
);

// ── Button ──────────────────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'add';

export interface ButtonProps extends Omit<ButtonBaseProps, 'children'> {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  icon?: IconName | ReactNode;
  endIcon?: IconName | ReactNode;
  block?: boolean;
  children?: ReactNode;
}

const BUTTON_VARIANTS: Record<ButtonVariant, Sx> = {
  // the one action (submit, sign in, send): a raised slate key with the app's top light and a soft drop, the label in
  // bright ink; no lime slab. Lifts a step on hover, the
  // theme's deboss when pressed, flat and dimmed when disabled
  primary: {
    backgroundColor: tx.card,
    backgroundImage: 'linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0) 60%)',
    color: tx.text,
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 6px 16px -8px rgba(0, 0, 0, 0.55)',
    '& .hx-primary-mark': { color: tx.lime },
    '&:hover': {
      backgroundColor: 'color-mix(in srgb, var(--tx-glass-card) 88%, white)',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 10px 22px -10px rgba(0, 0, 0, 0.6)',
    },
    '&:active': { boxShadow: tx.deboss },
    '&.Mui-disabled': {
      opacity: 1,
      backgroundColor: tx.fill,
      backgroundImage: 'none',
      boxShadow: 'none',
      color: tx.faint,
      '& .hx-primary-mark': { color: tx.faint },
    },
  },
  secondary: { backgroundColor: tx.fill, color: tx.text, '&:hover': { backgroundColor: tx.fill2 } },
  ghost: { backgroundColor: 'transparent', color: tx.text, '&:hover': { backgroundColor: tx.fill } },
  danger: { backgroundColor: tx.fill, color: tx.danger, '&:hover': { backgroundColor: tx.fill2 } },
  // an "add" on a page (Add client, Add property, Create event, Invite user): the tonal fill with a lime plus, the label
  // in muted ink that wakes on hover. Never a solid lime slab on a page
  add: {
    backgroundColor: tx.fill,
    color: tx.muted,
    '& .hx-add-glyph': { color: tx.lime },
    '&:hover': { backgroundColor: tx.fill2, color: tx.text },
  },
};

// secondary (the default tonal fill), primary (the one lime action on a screen), ghost (quiet), danger
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'secondary', size = 'md', icon, endIcon, block, children, sx, ...rest }, ref) => (
    <ButtonBase
      ref={ref}
      sx={merge(
        {
          height: size === 'sm' ? 32 : tx.h,
          px: variant === 'primary' ? (size === 'sm' ? '16px' : '24px') : size === 'sm' ? '14px' : '20px',
          gap: '8px',
          borderRadius: `${tx.rCtl}px`,
          fontFamily: 'inherit',
          fontSize: 'var(--tx-button-size)',
          fontWeight: 600,
          letterSpacing: '-0.02em',
          whiteSpace: 'nowrap',
          width: block ? '100%' : undefined,
          ...press,
        },
        BUTTON_VARIANTS[variant],
        sx,
      )}
      {...rest}>
      {variant === 'add' ? (
        <Box component="span" className="hx-add-glyph" sx={{ display: 'flex' }}>
          {glyph(icon ?? 'plus', 16)}
        </Box>
      ) : variant === 'primary' ? (
        // a submit carries its own icon in lime when it has one (Send); otherwise just its label
        icon ? (
          <Box component="span" className="hx-primary-mark" sx={{ display: 'flex' }}>
            {glyph(icon, 16)}
          </Box>
        ) : null
      ) : (
        glyph(icon, 16)
      )}
      {children}
      {variant === 'primary' && endIcon ? (
        <Box component="span" className="hx-primary-mark" sx={{ display: 'flex' }}>
          {glyph(endIcon, 16)}
        </Box>
      ) : (
        glyph(endIcon, 16)
      )}
    </ButtonBase>
  ),
);
Button.displayName = 'Button';

// ── IconButton ──────────────────────────────────────────────────────────────

export interface IconButtonProps extends Omit<ButtonBaseProps, 'children'> {
  icon: IconName | ReactNode;
  // required: the only name a screen reader gets, and the tooltip, since an icon alone doesn't say what it does
  label: string;
  size?: 'md' | 'sm';
  variant?: 'tonal' | 'ghost';
  on?: boolean;
  tipPlacement?: 'top' | 'bottom' | 'left' | 'right' | false;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, size = 'md', variant = 'tonal', on, tipPlacement = 'top', sx, ...rest }, ref) => {
    const button = (
      <ButtonBase
        ref={ref}
        aria-label={label}
        aria-pressed={on || undefined}
        sx={merge(
          {
            width: size === 'sm' ? 32 : tx.h,
            height: size === 'sm' ? 32 : tx.h,
            flex: 'none',
            borderRadius: `${size === 'sm' ? 10 : tx.rCtl}px`,
            color: tx.text,
            backgroundColor: variant === 'tonal' ? tx.fill : 'transparent',
            '&:hover': { backgroundColor: variant === 'tonal' ? tx.fill2 : tx.fill },
            ...press,
          },
          on && selectedSx,
          sx,
        )}
        {...rest}>
        {glyph(icon, size === 'sm' ? 16 : 18)}
      </ButtonBase>
    );
    return tipPlacement ? (
      <Tooltip title={label} placement={tipPlacement}>
        {button}
      </Tooltip>
    ) : (
      button
    );
  },
);
IconButton.displayName = 'IconButton';

// Close (X): a rounded square on the sheet's inset line. lg 36 for sheets and dialogs, sm 28 inside cards
export const CloseButton = ({
  onClose,
  size = 'lg',
  label = 'Close',
  sx,
}: {
  onClose: () => void;
  size?: 'lg' | 'sm';
  label?: string;
  sx?: Sx;
}) => (
  <ButtonBase
    aria-label={label}
    onClick={onClose}
    sx={merge(
      {
        width: size === 'lg' ? 36 : 28,
        height: size === 'lg' ? 36 : 28,
        flex: 'none',
        borderRadius: `${size === 'lg' ? 10 : 8}px`,
        color: tx.muted,
        '&:hover': { backgroundColor: tx.fill, color: tx.text },
        ...press,
      },
      sx,
    )}>
    <Icon name="x" size={size === 'lg' ? 18 : 14} />
  </ButtonBase>
);

// ── Fields ──────────────────────────────────────────────────────────────────

// Field: a label over its control, with an optional hint under it. aside sits on the label's line
export const Field = ({
  label,
  hint,
  htmlFor,
  aside,
  children,
  sx,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  htmlFor?: string;
  aside?: ReactNode;
  children: ReactNode;
  sx?: Sx;
}) => (
  <Box sx={merge({ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }, sx)}>
    {(label || aside) && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        {label && <Label htmlFor={htmlFor}>{label}</Label>}
        {aside}
      </Box>
    )}
    {children}
    {hint && <Meta>{hint}</Meta>}
  </Box>
);

export interface TextInputProps extends InputBaseProps {
  icon?: IconName;
  size?: 'medium' | 'small';
}

// TextInput: a tonal well that focuses by its fill alone; icon puts a leading glyph inside (search)
export const TextInput = forwardRef<HTMLDivElement, TextInputProps>(({ icon, size, sx, ...rest }, ref) => (
  <InputBase
    ref={ref}
    startAdornment={
      icon ? (
        <Box component="span" sx={{ display: 'flex', color: tx.faint, mr: '8px' }}>
          <Icon name={icon} size={16} />
        </Box>
      ) : undefined
    }
    sx={merge(
      {
        height: rest.multiline ? 'auto' : size === 'small' ? 32 : tx.h,
        minHeight: size === 'small' ? 32 : tx.h,
        px: '12px',
        py: rest.multiline ? '10px' : 0,
        borderRadius: `${tx.rCtl}px`,
        backgroundColor: tx.fill,
        transition: `background-color .15s ${tx.ease}`,
        fontSize: 'var(--tx-field-size)',
        fontWeight: 600,
        color: tx.text,
        '&:hover, &.Mui-focused': { backgroundColor: tx.fill2 },
        '&.Mui-error': { boxShadow: 'inset 0 0 0 1px var(--tx-danger)' },
        '& input, & textarea': { p: 0, fontSize: 'inherit', fontWeight: 'inherit' },
        '& input::placeholder, & textarea::placeholder': { color: tx.faint, fontWeight: 500, opacity: 1 },
      },
      sx,
    )}
    {...rest}
  />
));
TextInput.displayName = 'TextInput';

// ── Choice controls ─────────────────────────────────────────────────────────

export interface SegmentedProps<T extends string> {
  options: [T, ReactNode][];
  value: T;
  onChange: (value: T) => void;
  label?: string;
  size?: 'md' | 'sm';
  block?: boolean;
}

// Segmented: one choice of a few, as a tonal track with a pressed thumb
export const Segmented = <T extends string>({ options, value, onChange, label, size = 'md', block }: SegmentedProps<T>) => (
  <Box
    role="group"
    aria-label={label}
    sx={{
      display: block ? 'flex' : 'inline-flex',
      width: block ? '100%' : 'fit-content',
      gap: '2px',
      p: '3px',
      borderRadius: `${tx.rCtl}px`,
      backgroundColor: tx.fill,
    }}>
    {options.map(([v, l]) => (
      <ButtonBase
        key={v}
        aria-pressed={value === v}
        onClick={() => onChange(v)}
        sx={merge(
          {
            flex: block ? 1 : 'none',
            height: size === 'sm' ? 26 : 34,
            px: size === 'sm' ? '10px' : '14px',
            borderRadius: `${size === 'sm' ? 7 : tx.rThumb}px`,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: tx.muted,
            '&:hover': { color: tx.text },
            ...press,
          },
          value === v && selectedSx,
        )}>
        {l}
      </ButtonBase>
    ))}
  </Box>
);

// Chip: a filter or tag you can turn on; count is a small figure after the label
export const Chip = ({
  on,
  count,
  icon,
  children,
  onClick,
  sx,
}: {
  on?: boolean;
  count?: number;
  icon?: IconName;
  children: ReactNode;
  onClick?: () => void;
  sx?: Sx;
}) => (
  <ButtonBase
    aria-pressed={onClick ? !!on : undefined}
    onClick={onClick}
    disabled={!onClick}
    sx={merge(
      {
        height: 32,
        px: '12px',
        gap: '6px',
        borderRadius: `${tx.rCtl}px`,
        backgroundColor: tx.fill,
        color: tx.text,
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '-0.02em',
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: tx.fill2 },
        ...press,
        '&.Mui-disabled': { opacity: 1 },
      },
      on && selectedSx,
      sx,
    )}>
    {icon && <Icon name={icon} size={14} />}
    {children}
    {count != null && <Badge value={count} tone="neutral" />}
  </ButtonBase>
);

// Badge: a count. new (lime) for unread, neutral for a plain tally
export const Badge = ({ value, tone = 'new' }: { value: number | string; tone?: 'new' | 'neutral' }) => (
  <Box
    component="span"
    sx={{
      minWidth: 18,
      height: 18,
      px: '5px',
      display: 'inline-grid',
      placeItems: 'center',
      borderRadius: '6px',
      fontSize: 10.5,
      fontWeight: 600,
      lineHeight: 1,
      backgroundColor: tone === 'new' ? tx.lime : tx.ink(0.14),
      color: tone === 'new' ? '#262626' : tx.text,
    }}>
    {value}
  </Box>
);

// Check: a checkbox with its label beside it
export const Check = ({
  checked,
  onChange,
  children,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children?: ReactNode;
  disabled?: boolean;
}) => (
  <Box
    component="label"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '10px',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      fontSize: 13,
      fontWeight: 500,
      color: tx.text,
    }}>
    <Box
      component="input"
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      sx={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
    />
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 20,
        height: 20,
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: '6px',
        color: tx.onLime,
        backgroundColor: checked ? tx.lime : tx.fill2,
        boxShadow: checked ? 'none' : tx.deboss,
        'input:focus-visible + &': { outline: '2px solid var(--tx-focus)', outlineOffset: 2 },
      }}>
      {checked && <Icon name="check" size={12} stroke={2.6} />}
    </Box>
    {children && <span>{children}</span>}
  </Box>
);

export { Switch };

// ── Surfaces ────────────────────────────────────────────────────────────────

// Sheet: the raised surface every page section, panel and form sits on (glass on Light and Natural, matte on Dark)
export const Sheet = ({ children, sx, padded = true }: { children: ReactNode; sx?: Sx; padded?: boolean }) => (
  <Box
    component="section"
    sx={merge(
      {
        position: 'relative',
        backgroundColor: tx.panel,
        backgroundImage: tx.surfaceLight,
        backdropFilter: tx.blurPanel,
        WebkitBackdropFilter: tx.blurPanel,
        boxShadow: tx.shadow,
        borderRadius: `${tx.rSheet}px`,
        color: tx.text,
        p: padded ? 'var(--tx-inset)' : 0,
      },
      sx,
    )}>
    {children}
  </Box>
);

// SheetHeader: every sheet opens with this: the eyebrow over the light title, an optional sub line, the X top right
export const SheetHeader = ({
  eyebrow,
  title,
  sub,
  actions,
  onClose,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  onClose?: () => void;
}) => (
  <Box component="header" sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px', mb: '14px' }}>
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <Title>{title}</Title>
      {sub && <Meta sx={{ fontSize: 12 }}>{sub}</Meta>}
    </Box>
    {actions && <Box sx={{ display: 'flex', gap: '8px', flex: 'none' }}>{actions}</Box>}
    {onClose && <CloseButton onClose={onClose} sx={{ mt: '-4px', mr: '-6px' }} />}
  </Box>
);

// Card: a grouped block inside a sheet. A plain faint fill, no edge, no shadow; never a box inside a box
export const Card = ({
  title,
  step,
  aside,
  children,
  sx,
}: {
  title?: ReactNode;
  step?: ReactNode;
  aside?: ReactNode;
  children?: ReactNode;
  sx?: Sx;
}) => (
  <Box
    component="section"
    sx={merge(
      {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        p: '12px',
        borderRadius: `${tx.rTile}px`,
        backgroundColor: tx.ink(0.045),
        color: tx.text,
      },
      sx,
    )}>
    {(title || aside) && (
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
        {step && <Box sx={{ fontSize: 11, fontWeight: 600, color: tx.faint }}>{step}</Box>}
        {title && (
          <Box sx={{ flex: 1, minWidth: 0, fontSize: 18, fontWeight: 400, letterSpacing: '-0.02em' }}>{title}</Box>
        )}
        {aside && <Box sx={{ flex: 'none', fontSize: 12, color: tx.muted }}>{aside}</Box>}
      </Box>
    )}
    {children}
  </Box>
);

// Tile: a large tap target with an icon over its name (a menu of moves, a grid of tools)
export const Tile = ({
  icon,
  label,
  sub,
  on,
  onClick,
}: {
  icon?: IconName;
  label: ReactNode;
  sub?: ReactNode;
  on?: boolean;
  onClick?: () => void;
}) => (
  <ButtonBase
    aria-pressed={on || undefined}
    onClick={onClick}
    sx={merge(
      {
        minHeight: 84,
        p: '14px 16px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '10px',
        borderRadius: `${tx.rTile}px`,
        backgroundColor: tx.fill,
        color: tx.text,
        textAlign: 'left',
        '&:hover': { backgroundColor: tx.fill2 },
        ...press,
      },
      on && selectedSx,
    )}>
    {icon && <Icon name={icon} size={22} />}
    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Box component="b" sx={{ fontSize: 15, fontWeight: 500 }}>
        {label}
      </Box>
      {sub && <Meta>{sub}</Meta>}
    </Box>
  </ButtonBase>
);

// Stat: a light, tabular figure over what it measures. StatRow lays several out split by hairlines
// Stat: a light figure over its label (Tradehaus .tw-fig): 200 weight, the digits set close, the label in meta
export const Stat = ({
  value,
  label,
  size = 'md',
  align = 'left',
  sx,
}: {
  value: ReactNode;
  label: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  align?: 'left' | 'right' | 'center';
  sx?: Sx;
}) => (
  <Box sx={merge({ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0, textAlign: align }, sx)}>
    <Box
      component="b"
      sx={{
        fontSize: { sm: 22, md: 28, lg: 44 }[size],
        fontWeight: 200,
        letterSpacing: '-0.05em',
        lineHeight: 1,
        fontVariantNumeric: 'proportional-nums',
        color: tx.text,
      }}>
      {value}
    </Box>
    <Meta sx={{ fontSize: 11, fontWeight: 500, color: tx.muted }}>{label}</Meta>
  </Box>
);

// StatRow: stats side by side, parted by hairlines, no box behind them
export const StatRow = ({ children, gap = 20 }: { children: ReactNode; gap?: number }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      '& > *': { px: `${gap}px` },
      '& > *:first-of-type': { pl: 0 },
      '& > *:last-of-type': { pr: 0 },
      '& > * + *': { borderLeft: `1px solid ${tx.line}` },
    }}>
    {children}
  </Box>
);

// Tag: a small label as a rounded square (Tradehaus .tx-pill), never a capsule. Neutral sits on the theme fill;
// a colour tints the fill and inks the text
export const Tag = ({ children, color, sx }: { children: ReactNode; color?: string; sx?: Sx }) => (
  <Box
    component="span"
    sx={merge(
      {
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: '8px',
        borderRadius: '6px',
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        backgroundColor: color ? `color-mix(in srgb, ${color} 14%, transparent)` : tx.fill2,
        color: color ?? tx.text,
      },
      sx,
    )}>
    {children}
  </Box>
);

// LegendItem: a series named the data's own way: its hairline in the series colour, the name in meta, the count in ink
export const LegendItem = ({ color, label, count }: { color: string; label: ReactNode; count?: ReactNode }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 11, fontWeight: 500, color: tx.muted }}>
    <Box component="span" sx={{ width: 2, height: 12, borderRadius: '1px', backgroundColor: color }} />
    <span>{label}</span>
    {count != null && (
      <Box component="span" sx={{ color: tx.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {count}
      </Box>
    )}
  </Box>
);

// TipCard: a hover card (Tradehaus .tx-tip): an eyebrow with an optional hairline, the title, then a light figure with
// meta beside it. Position it with sx
export const TipCard = ({
  eyebrow,
  color,
  title,
  figure,
  meta,
  sx,
}: {
  eyebrow?: ReactNode;
  color?: string;
  title: ReactNode;
  figure?: ReactNode;
  meta?: ReactNode;
  sx?: Sx;
}) => (
  <Box
    sx={merge(
      {
        width: 280,
        p: '12px 14px',
        borderRadius: '12px',
        background: 'var(--tx-glass)',
        backdropFilter: 'var(--tx-blur)',
        WebkitBackdropFilter: 'var(--tx-blur)',
        boxShadow: tx.shadowSm,
        pointerEvents: 'none',
      },
      sx,
    )}>
    {eyebrow && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 11, fontWeight: 500, color: tx.muted, mb: '6px' }}>
        {color && <Box component="span" sx={{ width: 2, height: 12, borderRadius: '1px', backgroundColor: color }} />}
        <span>{eyebrow}</span>
      </Box>
    )}
    <Box sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, color: tx.text }}>{title}</Box>
    {(figure || meta) && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', mt: '10px' }}>
        <Box component="span" sx={{ fontSize: 15, fontWeight: 300, letterSpacing: '-0.02em', lineHeight: 1, color: tx.text }}>
          {figure}
        </Box>
        <Box component="span" sx={{ fontSize: 11, fontWeight: 500, color: tx.muted, whiteSpace: 'nowrap' }}>
          {meta}
        </Box>
      </Box>
    )}
  </Box>
);

export const Divider = ({ sx }: { sx?: Sx }) => (
  <Box component="hr" sx={merge({ m: 0, border: 0, borderTop: `1px solid ${tx.line}` }, sx)} />
);

// ── Tables ──────────────────────────────────────────────────────────────────

// SortCaret: the one sort indicator for every column header. Two small stacked triangles; the direction in use is
// inked, the other (and both, on an unsorted column) stays faint. Sits 5px after the header label
export const SortCaret = ({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) => (
  <Box
    component="svg"
    aria-hidden
    width={8}
    height={11}
    viewBox="0 0 8 11"
    sx={{ ml: '5px', flexShrink: 0, display: 'inline-block', verticalAlign: 'middle' }}>
    <path d="M4 0.5 L0.5 4.5 L7.5 4.5 Z" fill={active && dir === 'asc' ? tx.text : tx.faint} opacity={active && dir === 'asc' ? 1 : 0.4} />
    <path d="M4 10.5 L0.5 6.5 L7.5 6.5 Z" fill={active && dir === 'desc' ? tx.text : tx.faint} opacity={active && dir === 'desc' ? 1 : 0.4} />
  </Box>
);

// ── Navigation ──────────────────────────────────────────────────────────────

// NavItem: a sidebar row. The current page is milled in; collapsed shows the icon alone with its label as tooltip
export const NavItem = ({
  icon,
  label,
  active,
  badge,
  disabled,
  collapsed,
  trailing,
  onClick,
  testId,
}: {
  icon?: ReactNode;
  label: string;
  active?: boolean;
  badge?: number;
  disabled?: boolean;
  collapsed?: boolean;
  trailing?: ReactNode;
  onClick?: () => void;
  testId?: string;
}) => {
  const row = (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-current={active ? 'page' : undefined}
      aria-disabled={disabled || undefined}
      sx={merge(
        {
          width: '100%',
          height: 36,
          px: collapsed ? 0 : '10px',
          gap: '10px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderRadius: `${tx.rCtl}px`,
          color: tx.muted,
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: '-0.01em',
          textAlign: 'left',
          '&:hover': { backgroundColor: tx.fill, color: tx.text },
          ...press,
        },
        // the current page: a flat tonal tile in full ink (Tradehaus rows), no raised shadow
        active && { backgroundColor: tx.fill2, color: tx.text, fontWeight: 600 },
      )}>
      {icon && <Box sx={{ display: 'flex', flex: 'none', width: 18, justifyContent: 'center' }}>{icon}</Box>}
      {!collapsed && (
        <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Box>
      )}
      {!collapsed && trailing}
      {badge != null && badge > 0 && <Badge value={badge} />}
    </ButtonBase>
  );
  return collapsed ? (
    <Tooltip title={label} placement="right">
      {row}
    </Tooltip>
  ) : (
    row
  );
};

// NavSection: a sentence-case eyebrow over a group of NavItems
export const NavSection = ({ label, children, trailing, onToggle }: {
  label: ReactNode;
  children?: ReactNode;
  trailing?: ReactNode;
  onToggle?: () => void;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        px: '10px',
        pt: '6px',
        pb: '8px',
        fontSize: 12,
        fontWeight: 600,
        color: tx.faint,
        cursor: onToggle ? 'pointer' : 'default',
        userSelect: 'none',
      }}>
      <Box sx={{ flex: 1 }}>{label}</Box>
      {trailing}
    </Box>
    {children}
  </Box>
);

// ── People ──────────────────────────────────────────────────────────────────

const initials = (name?: string, email?: string) => {
  const src = (name || '').trim() || (email || '').split('@')[0] || '';
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  return parts.length ? (parts[0][0] + (parts[1] ? parts[1][0] : '')).toUpperCase() : '';
};

// Avatar: people are circles. Initials are the normal state, not a fallback
export const Avatar = ({ name, email, src, size = 32 }: { name?: string; email?: string; src?: string; size?: number }) => (
  <Box
    component="span"
    sx={{
      width: size,
      height: size,
      flex: 'none',
      borderRadius: '50%',
      overflow: 'hidden',
      display: 'grid',
      placeItems: 'center',
      backgroundColor: tx.fill2,
      color: tx.text,
      fontSize: Math.round(size * 0.38),
      fontWeight: 600,
    }}>
    {src ? <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials(name, email)}
  </Box>
);

export { Icon };
export type { IconName };
